import axios from 'axios';
import store from 'root-js/store';

function isLoginRequired(error) {
    const apiError = error.response?.data?.error;

    return (typeof apiError === 'string' ? apiError : (
        apiError?.meta?.oauth_error || apiError?.meta?.reason || apiError?.code || apiError?.reason
    )) === 'login_required';
}

/**
 * Handle all API requests.
 *
 * @author      Ben Carey <bdmc@sinemacula.co.uk>
 * @copyright   2022 Sine Macula Limited.
 */
export default class API {

    /**
     * API constructor.
     *
     * @param {string}  url
     * @param {EndpointCollection}  endpoints
     * @param {string|null}  token
     * @param {object}  callbacks
     */
    constructor(url, endpoints, token = null, callbacks = {}) {
        this.endpoints = endpoints;

        // Create local instance of axios
        this.axios = axios.create({
            baseURL: url,
            headers: { 'X-Requested-With': 'XMLHttpRequest' }
        });

        this.callbacks = callbacks;

        // Set the authentication token

        this._interceptors();
    }

    /**
     * Set Interceptors
     */
    _interceptors() {
        let refreshPromise = null;

        const refreshToken = () => {
            if (!refreshPromise) {
                refreshPromise = store.dispatch('auth/REFRESH_TOKEN')
                    .then(refreshResult => {
                        if (!refreshResult?.token) {
                            throw new Error('Token refresh failed.');
                        }

                        return refreshResult.token;
                    })
                    .catch(async error => {
                        await store.dispatch('auth/LOGOUT');
                        throw error;
                    })
                    .finally(() => {
                        refreshPromise = null;
                    });
            }

            return refreshPromise;
        };

        this.axios.interceptors.request.use(config => {

            const token = localStorage.getItem('token');
            if (config.public) {
                if (config.bearerToken) {
                    config.headers.Authorization = `Bearer ${config.bearerToken}`;
                } else {
                    delete config.headers.Authorization;
                }

                return config;
            }

            if (token && !config.skipAuth) {
                config.headers.Authorization = `Bearer ${token}`;
            }

            return config;
        },
        error => Promise.reject(error));


        this.axios.interceptors.response.use(response => response,
            async error => {
                const originalRequest = error.config;

                if (originalRequest?.skipInterceptor || originalRequest?.skipAuth || originalRequest?.public) {

                    return Promise.reject(error);
                }

                if (
                    originalRequest?.skipRefreshOnLoginRequired
                    && isLoginRequired(error)
                ) {
                    return Promise.reject(error);
                }

                if (error.response?.status === 401 && !originalRequest._retry) {
                    originalRequest._retry = true;
                    const token = await refreshToken();

                    originalRequest.headers = originalRequest.headers || {};
                    originalRequest.headers.Authorization = `Bearer ${token}`;

                    return this.axios(originalRequest);
                }

                return Promise.reject(error);
            });
    }


    /**
     * Set the authentication token.
     *
     * @param {string|null}  token
     */
    setToken(token = null) {

        if (token) {
            this.axios.defaults.headers.common.Authorization = `Bearer ${token}`;
        } else {
            delete this.axios.defaults.headers.common.Authorization;
        }
    }

    /**
     * Call an API endpoint by name.
     *
     * @param {string}  name
     * @param {object}  data
     * @param {string|null}  fields
     * @param {object}  options
     * @param {boolean}  ignoreEmpty
     * @returns {Promise}
     */
    call(name, data = {}, fields = null, options = {}, ignoreEmpty = false, skipInterceptor = false) {
        const endpoint = this.endpoints.find(name);

        if (!endpoint) {
            throw new Error('Invalid endpoint supplied');
        }

        const request = Object.assign(options,
            this._build(endpoint, data, fields, ignoreEmpty));

        if (skipInterceptor) {

            request.skipInterceptor = true;


        }

        return this._execute(request);
    }

    /**
     * Build the options and configuration for the axios API request.
     *
     * @param {object}  endpoint
     * @param {object}  data
     * @param {string|null}  fields
     * @param {boolean}  ignoreEmpty
     * @returns {object}
     */
    _build(endpoint, data = {}, fields = null, ignoreEmpty = false) {
        const request = {};

        request.method = endpoint.method();
        request.url = endpoint.path(data);
        request.responseType = endpoint.responseType();

        let parameters = {};

        /*
         * Fields define what fields should be returned for each object from the
         * API. If they have been supplied then we need to append them to the
         * request parameters
         */
        if (fields) {
            parameters.fields = fields;
        }

        /*
         * Loop through the data object and remove any empty fields
         */
        if (ignoreEmpty) {
            Object.keys(data).forEach(key => (data[key] === null || data[key] === '') && delete data[key]);
        }

        let formData = false;

        /*
         * If any the of values within data are files then we need to change the
         * request to accept file uploads
         */
        if (!Object.values(data).every(value => !(value instanceof File))) {
            formData = new FormData;

            Object.entries(data).forEach(([key, value]) => {
                formData.append(key, value);
            });

            request.headers = { 'content-type': 'multipart/form-data' };
        }

        /*
         * Dependent on the type of request, we either attach the data to the
         * request data or merge with the request parameters
         */
        if (['put', 'post', 'patch'].includes(request.method)) {
            request.data = formData instanceof FormData ? formData : data;
        } else {
            parameters = Object.assign(data, parameters);
        }

        request.params = parameters;

        return request;
    }

    /**
     * Execute the specified request.
     *
     * @param {object}  request
     * @returns {Promise}
     */
    _execute(request) {
        return this.axios
            .request(request)
            .catch(error => {
                if (
                    error.response
                    && Object.prototype.hasOwnProperty.call(error.response,
                        'status')
                    && !error.config?.skipAuth
                    && !error.config?.public
                ) {
                    this._resolveCallback(error.response.status, error);
                }

                throw error;
            })
            .then(response => {
                this._resolveCallback(response.status, response);

                return response;
            });
    }

    /**
     * Resolve the custom callback.
     *
     * @param {int}  code
     * @param {object}  data
     * @returns {void}
     */
    _resolveCallback(code, data = {}) {

        // Run callback for all requests if the callback exists
        if (Object.prototype.hasOwnProperty.call(this.callbacks, 'all')) {
            this.callbacks.all(data);
        }

        // Run specific callback
        if (Object.prototype.hasOwnProperty.call(this.callbacks, code)) {
            this.callbacks[code](data);
        }
    }
}
